"use client";

import { useNavigation } from "@app/[locale]/(site)/navContext";
import type { Filter as FilterType } from "@server/utils/parseLoop/types";
import type { ChangeEvent } from "react";
import { useMemo } from "react";
import type { Dictionary } from "types";

import { envConfig } from "@/config/env";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import getLeafCategories from "@/utils/navigation/getLeafCategories";

import Checkbox from "./checkbox";
import ExponentialRange from "./ExponentialRange";
import Range from "./Range";
import Wrapper from "./wrapper";

type FacetProps = {
  option: FilterType;
  onChange: (event: ChangeEvent<HTMLInputElement>, attribute: string) => void;
  dictionary: Dictionary;
  slug?: string;
};

function getLabel(option: FilterType, dictionary: Dictionary) {
  if (FEATURE_FLAGS.elevate) {
    let key = option.attribute ?? "";
    if (key === "custom.leafCategoryCodes") key = "category";
    if (key === "custom.statuses") key = "featured";
    if (key === "custom.designerName") key = "designer";
    if (key === "custom.norce_color_group") key = "norce_color_group";
    return dictionary[key] ?? option.name;
  }
  const key = option.name ?? "";
  return (dictionary[key.toLowerCase()] || dictionary[key]) ?? key;
}

const Facet = (props: FacetProps) => {
  const { option, onChange, dictionary } = props;

  if (
    option.type === "range" &&
    option.min !== undefined &&
    option.max !== undefined
  ) {
    const { min, max, values, attribute } = option;

    if (
      envConfig.NEXT_PUBLIC_FEATURES_EXPONENTIAL_PRICE_FILTER === "true" &&
      attribute.toLowerCase() === "price"
    ) {
      return (
        <ExponentialRange
          {...option}
          min={min}
          max={max}
          values={values}
          attribute={attribute}
          suffix={option.extras?.suffix}
          minLabel={
            option.extras?.minLabel
              ? dictionary[option.extras.minLabel]
              : undefined
          }
          maxLabel={
            option.extras?.maxLabel
              ? dictionary[option.extras.maxLabel]
              : undefined
          }
          onChange={onChange}
        />
      );
    }

    return (
      <Range
        {...option}
        min={min}
        max={max}
        values={values}
        attribute={attribute}
        suffix={option.extras?.suffix}
        minLabel={
          option.extras?.minLabel
            ? dictionary[option.extras.minLabel]
            : undefined
        }
        maxLabel={
          option.extras?.maxLabel
            ? dictionary[option.extras.maxLabel]
            : undefined
        }
        onChange={onChange}
      />
    );
  }

  if (!option.items?.length) {
    return null;
  }

  if (option.type === "checkbox") {
    return <Checkbox {...option} onChange={onChange} dictionary={dictionary} />;
  }

  return null;
};

type Props = {
  dictionary: Dictionary;
  filter: FilterType[];
  onChange: any;
  slug?: string;
};
export default function Filter(props: Props) {
  const { dictionary, filter, onChange, slug = "root" } = props;
  const { flatNavigation, commerceSlugPairs } = useNavigation().state;
  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    attribute: string,
  ) => {
    const { target } = event;
    const localFilters = [...filter];
    const index = localFilters.findIndex(
      (facet) => facet.attribute === attribute,
    );
    const localFilter = { ...localFilters[index] };

    if (localFilter.type === "range") {
      const { min, max } = target;
      localFilter.values = [Number(min), Number(max)];
    } else if (target.checked && localFilter.type === "checkbox") {
      localFilter.values = [...(localFilter.values ?? []), target.name];
    } else if (localFilter.values?.length) {
      localFilter.values = localFilter.values.filter(
        (value: string) => value !== target.name,
      );
    }

    localFilters.splice(index, 1, localFilter);

    onChange(localFilters);
  };

  const displayedFilters = useMemo<FilterType[]>(
    () =>
      [...filter].map((f) => {
        if (FEATURE_FLAGS.elevate) {
          return f;
        }
        if (f.name !== "Category" || !f.items) return f;
        const currentCategory = flatNavigation?.[slug]?.children?.length
          ? flatNavigation?.[slug]
          : flatNavigation?.root;

        if (!currentCategory?.children?.length) {
          return {
            ...f,
            items: [],
          };
        }

        const actualCategories = getLeafCategories(
          currentCategory,
          flatNavigation as any,
        );

        const items = f.items.filter((item: any) => {
          const _slug = commerceSlugPairs?.[item.name];

          return item.count > 0 && actualCategories.includes(_slug as string);
        });

        return {
          ...f,
          items,
        };
      }),
    [slug, filter, flatNavigation, commerceSlugPairs],
  );

  const visibleFilterCount = displayedFilters.filter(
    (option: FilterType) =>
      option.show && (!option.items || option.items?.length > 1),
  ).length;

  return (
    <div className="grid w-full grid-flow-row overflow-hidden border-grey-150 px-3 hr-y last:child:border-b">
      {displayedFilters.map((option: FilterType) => {
        let shouldOpen =
          option.name === "Category" ||
          option.name === "featured" ||
          visibleFilterCount <= 3;

        if (FEATURE_FLAGS.elevate) {
          shouldOpen =
            option.attribute === "custom.leafCategoryCodes" ||
            option.attribute === "custom.statuses";
        }

        if (option.name !== "featured" && option.items?.length === 1) {
          return null;
        }

        return (
          option.show && (
            <Wrapper
              open={shouldOpen}
              key={option.attribute}
              label={getLabel(option, dictionary)}
              slug={slug}
              totalSelected={
                option.type === "checkbox" && option?.values?.length
              }
            >
              <Facet
                option={option}
                slug={slug}
                dictionary={dictionary}
                onChange={handleChange}
              />
            </Wrapper>
          )
        );
      })}
    </div>
  );
}
